import { useEffect, useState } from "react";
import { agentApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import {
  GROUP_META, GROUP_ORDER, purposeLabel, TIER_LABELS,
  sourceBadge, providerGlyph,
} from "./modelPurposeLabels";

// Agent Model Transparency v1 — "Models this agent uses" (RC-1/RC-2/RC-3/RD-1).
//
// Read-only. Fetches GET /api/agents/{id}/model-resolution — which runs the
// SAME BYOK resolver the runtime uses (INV-ONE-RESOLVER) — and renders one row
// per purpose (+ smart-routing tiers when enabled), grouped into Conversation /
// Behind the scenes / Reading images. Each row shows a plain-language label, the
// model + provider glyph, and a source badge. A capability mismatch (a
// non-vision model on a vision purpose) gets an amber "⚠ Check this" chip (RD-1);
// an unconfigured purpose gets a muted "Not configured — set in LLM Rankings →"
// deep-link (RC-2). The card computes NOTHING about resolution itself — it
// renders exactly what the API returns.
//
// Conformance spec: docs/design/agent-model-transparency-mockup-v1.html.

const RANKINGS_DEEP_LINK = "/admin?tab=rankings";

const BADGE_STYLE = {
  local: { color: COLORS.purple, bg: `${COLORS.purple}1c`, border: `${COLORS.purple}66` },
  agent: { color: COLORS.accentText, bg: `${COLORS.accent}22`, border: `${COLORS.accent}66` },
  tenant: { color: COLORS.text, bg: COLORS.bg, border: COLORS.border },
  none: { color: COLORS.textDim, bg: COLORS.bg, border: `${COLORS.border}` },
};

function SourceBadge({ row }) {
  const b = sourceBadge(row);
  const s = BADGE_STYLE[b.tone] || BADGE_STYLE.none;
  // Lifted out of the style object so v8 credits the branch on a plain statement.
  const borderStyle = b.tone === "none" ? "dashed" : "solid";
  return (
    <span data-testid={`model-source-${b.tone}`} style={{
      fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 999,
      whiteSpace: "nowrap", color: s.color, background: s.bg,
      border: `1px solid ${s.border}`, borderStyle,
    }}>{b.text}</span>
  );
}

function ProviderGlyph({ provider }) {
  const g = providerGlyph(provider);
  return (
    <span aria-hidden style={{
      width: 22, height: 22, borderRadius: 6, display: "inline-grid",
      placeItems: "center", fontSize: 10, fontWeight: 800, color: "#0c0d18",
      background: g.hue, flex: "0 0 auto",
    }}>{g.glyph}</span>
  );
}

function ModelRow({ row }) {
  const isTier = !!row.tier;
  const meta = isTier ? (TIER_LABELS[row.tier] || { label: row.tier, hint: "" })
                      : purposeLabel(row.purpose);
  const unconfigured = !row.model;
  const mismatch = row.capability_ok === false;
  // Lifted out of JSX so v8 credits each branch on a plain statement line.
  const hintText = mismatch && row.note ? row.note : meta.hint;
  const unconfiguredLabel =
    row.note && row.note !== "not configured" ? row.note : "Not configured";
  return (
    <div
      data-testid={`model-row-${row.tier ? `tier-${row.tier}` : row.purpose}`}
      title={`purpose: ${row.purpose}${row.tier ? ` · tier: ${row.tier}` : ""}`}
      style={{
        display: "flex", alignItems: "center", gap: 12, padding: "11px 12px",
        borderRadius: 10, marginBottom: 8, background: COLORS.surface,
        border: `1px solid ${mismatch ? `${COLORS.orange}66` : COLORS.border}`,
      }}
    >
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 650, color: COLORS.text }}>{meta.label}</span>
          {mismatch && (
            <span data-testid="model-mismatch-chip" style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 10.5, fontWeight: 700, color: COLORS.orange,
              background: `${COLORS.orange}1a`, border: `1px solid ${COLORS.orange}55`,
              borderRadius: 999, padding: "3px 9px",
            }}>⚠ Check this</span>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: COLORS.textDim, marginTop: 2 }}>
          {hintText}
          {" · "}
          <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            {row.purpose}
          </span>
        </div>
      </div>

      {unconfigured ? (
        <a href={RANKINGS_DEEP_LINK} data-testid="model-unconfigured-link" style={{
          fontSize: 11.5, fontWeight: 650, color: COLORS.accentText, textDecoration: "none",
          whiteSpace: "nowrap",
        }}>
          {unconfiguredLabel} · Set in LLM Rankings →
        </a>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
          <ProviderGlyph provider={row.provider} />
          <span style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 12.5, color: COLORS.text,
          }}>{row.model}</span>
        </div>
      )}

      {!unconfigured && <SourceBadge row={row} />}
    </div>
  );
}

function Section({ groupKey, rows }) {
  if (!rows.length) return null;
  const meta = GROUP_META[groupKey] || { title: groupKey, icon: "•" };
  return (
    <div data-testid={`model-section-${groupKey}`} style={{ marginTop: 12 }}>
      <div style={{
        fontSize: 10.5, fontWeight: 800, letterSpacing: "0.07em",
        textTransform: "uppercase", color: COLORS.textDim,
        display: "flex", alignItems: "center", gap: 8, margin: "6px 0 8px",
      }}>
        <span aria-hidden>{meta.icon}</span> {meta.title}
      </div>
      {rows.map((r, i) => <ModelRow key={`${r.purpose}-${r.tier || ""}-${i}`} row={r} />)}
    </div>
  );
}

export default function ModelsCard({ agentId }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!agentId) return;
    setLoading(true);
    setErr(null);
    agentApi.modelResolution(agentId)
      .then(res => { if (!cancelled) setRows(res?.data?.rows || []); })
      .catch(e => { if (!cancelled) setErr(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agentId]);

  // Lifted out of the style object so v8 credits the branch on a plain statement.
  const cardShadow = MATERIALS.spatialShadow ? MATERIALS.spatialShadow() : undefined;
  return (
    <div data-testid="models-card" style={{
      padding: 16, borderRadius: 12,
      border: `1px solid ${COLORS.border}`, background: COLORS.surface,
      boxShadow: cardShadow,
    }}>
      <div style={{ fontSize: 15, fontWeight: 750, color: COLORS.text }}>
        Models this agent uses
      </div>
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2, lineHeight: 1.5 }}>
        Every job this agent does can use a different model — the model it replies with,
        plus the models working behind the scenes. This is the live resolution, not a guess.
      </div>

      {loading && !rows && (
        <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 12 }}>Loading models…</div>
      )}
      {err && !rows && (
        <div style={{ fontSize: 12, color: COLORS.pink, marginTop: 12 }}>
          Couldn't load model resolution: {err}
        </div>
      )}

      {rows && GROUP_ORDER.map(g => (
        <Section key={g} groupKey={g} rows={rows.filter(r => r.group === g)} />
      ))}

      <div style={{
        display: "flex", gap: 10, alignItems: "flex-start", marginTop: 14,
        fontSize: 12, color: COLORS.textMuted,
        background: `${COLORS.accent}12`, border: `1px solid ${COLORS.accent}44`,
        borderRadius: 10, padding: "11px 13px",
      }}>
        <span aria-hidden>🧭</span>
        <div>
          <strong>One source of truth.</strong> These rows are exactly what the platform
          resolves at runtime. To change any of them, edit{" "}
          <a href={RANKINGS_DEEP_LINK} style={{ color: COLORS.accentText, fontWeight: 650, textDecoration: "none" }}>
            LLM Rankings
          </a>{" "}
          (tenant defaults) or the agent's LLM node in the Builder (chat override).
        </div>
      </div>
    </div>
  );
}
